import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Puede tratarse la depresión sin más medicación?",
    answer:
      "Sí. Las opciones incluyen psicoterapia, TMS tradicional, TMS acelerada (SAINT®) y otros enfoques no sistémicos. Algunos pacientes pueden seguir usando medicación de forma apropiada como parte del tratamiento. El enfoque adecuado depende de su historial y debe comentarse con un psiquiatra.",
  },
  {
    question: "¿Es la TMS una medicación?",
    answer:
      "No. La TMS es un tratamiento con dispositivo médico, no un fármaco. Usa pulsos magnéticos para estimular una región cerebral específica y no introduce ningún producto químico en el cuerpo. Los dispositivos de TMS están autorizados por la FDA (cleared), no aprobados por la FDA (approved, término usado para medicamentos). Muchas personas continúan un antidepresivo durante la TMS, pero la TMS en sí puede administrarse sin medicación cuando un médico lo considera apropiado.",
  },
  {
    question: "¿Puedo suspender mi antidepresivo si empiezo la TMS?",
    answer:
      "Por su cuenta, no. Suspender de forma brusca puede causar síntomas de retirada y aumentar el riesgo de recaída. Si desea reducir o suspender la medicación, pida a su médico que planifique una reducción gradual y que decida la programación relativa a la TMS. La TMS suele administrarse junto con la medicación.",
  },
  {
    question: "¿Qué tratamientos sin medicación están disponibles?",
    answer:
      "Las opciones sin medicación incluyen psicoterapia (TCC, activación conductual, terapia interpersonal), TMS tradicional, TMS acelerada (SAINT®) y apoyo al estilo de vida como cuidado coadyuvante. El TEC también es sin medicación pero se reserva para casos graves. Cada una tiene evidencia, riesgos y idoneidad distintos.",
  },
  {
    question: "¿Es 'anti-medicación' considerar estas opciones?",
    answer:
      "No. Considerar opciones sin medicación no es anti-medicación. Algunos pacientes se benefician de la medicación; otros prefieren o necesitan alternativas. Muchos planes de tratamiento combinan medicación y enfoques sin medicación. El objetivo es encontrar lo que funciona para usted, guiado por un psiquiatra.",
  },
];

export const esBeyondMedication = makeEsArticle({
  slug: "depression-treatment-without-more-medication",
  title:
    "Tratamiento de la depresión más allá de más medicación | Neuro Wellness",
  description:
    "Tratamiento de la depresión más allá de añadir otra medicación: psicoterapia, TMS, SAINT® y enfoques no sistémicos. Algunos pacientes pueden seguir usando medicación. No es anti-medicación.",
  h1: "Opciones de tratamiento de la depresión más allá de añadir otra medicación",
  breadcrumbLabel: "Tratamiento más allá de la medicación",
  shortAnswer:
    "Si ha probado varios antidepresivos y busca opciones más allá de añadir otra medicación, existen varios enfoques con evidencia. Estos incluyen psicoterapia, TMS tradicional, TMS acelerada (SAINT®) y otros tratamientos no sistémicos que no implican añadir otro fármaco. Algunos pacientes pueden seguir usando medicación de forma apropiada como parte de un plan integral — considerar estas opciones no es anti-medicación. Esta página explica las opciones sin medicación disponibles y aclara que la medicación puede seguir desempeñando un papel en su tratamiento.",
  keyTakeaways: [
    "Existen opciones más allá de añadir otra medicación.",
    "La psicoterapia, la TMS y SAINT® son enfoques sin medicación.",
    "Algunos pacientes pueden seguir usando medicación de forma apropiada.",
    "Considerar alternativas no es anti-medicación.",
    "Una evaluación psiquiátrica guía el enfoque adecuado.",
  ],
  sections: [
    {
      heading: "Psicoterapia",
      paragraphs: [
        "La psicoterapia — incluida la TCC, la activación conductual y la terapia interpersonal — es eficaz para la depresión y no implica medicación. Puede usarse junto con medicación o por sí sola. La psicoterapia aborda patrones, habilidades y comportamientos subyacentes. Si no ha probado la psicoterapia, puede merecer la pena añadirla a su plan.",
        "Más información sobre [[psicoterapia|/es/services/psychotherapy]].",
      ],
    },
    {
      heading: "Apoyo al estilo de vida como cuidado coadyuvante",
      paragraphs: [
        "El ejercicio regular, el sueño constante, la exposición a la luz solar, la reducción del alcohol y el contacto social tienen evidencia para los síntomas depresivos. Son coadyuvantes — adiciones útiles más que tratamientos únicos para la depresión de moderada a grave. Apoyan el tratamiento general pero no sustituyen la atención clínica cuando los síntomas son significativos.",
      ],
    },
    {
      heading: "TMS tradicional",
      paragraphs: [
        "La TMS tradicional está autorizada por la FDA para adultos con trastorno depresivo mayor que no han mejorado con antidepresivos. Es un tratamiento con dispositivo médico — no un fármaco — que usa pulsos magnéticos para estimular la corteza prefrontal. Se administra una sesión por día durante unas seis semanas y está ampliamente cubierta por los seguros.",
        "Más información sobre [[terapia TMS|/es/learn/what-is-tms-therapy-for-depression]].",
      ],
    },
    {
      heading: "TMS acelerada (SAINT®)",
      paragraphs: [
        "SAINT® es un protocolo de TMS acelerada autorizado por la FDA que se administra durante cinco días consecutivos con orientación por fMRI. Al igual que la TMS tradicional, es un tratamiento con dispositivo, no un fármaco. Es más rápida en su administración y normalmente de pago directo. La respuesta individual varía.",
        "Más información sobre [[SAINT® TMS|/es/learn/what-is-saint-tms]].",
      ],
    },
    {
      heading: "Otros enfoques de tratamiento no sistémicos",
      paragraphs: [
        "Otros enfoques no sistémicos incluyen Spravato (esketamina), que es una medicación pero actúa de forma distinta a los antidepresivos orales, y el TEC para casos graves. Estos no son 'sin medicación' en sentido estricto, pero pueden considerarse cuando los antidepresivos orales no han funcionado. Comente todas las opciones con su psiquiatra.",
      ],
    },
    {
      heading: "La medicación puede seguir desempeñando un papel",
      paragraphs: [
        "Considerar opciones sin medicación no es anti-medicación. Algunos pacientes se benefician de la medicación; otros prefieren o necesitan alternativas. Muchos planes de tratamiento combinan medicación y enfoques sin medicación — por ejemplo, continuar un antidepresivo durante la TMS. El objetivo es encontrar lo que funciona para usted, guiado por un psiquiatra. No suspenda la medicación por su cuenta.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cuijpers P, et al. The effects of psychotherapy for adult depression are overestimated. Psychol Med. 2014;44(1):211-223.",
      url: "https://pubmed.ncbi.nlm.nih.gov/23432814/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
